import { useState, useRef, useEffect, useImperativeHandle, forwardRef, KeyboardEvent } from 'react';

import { useDebounce } from 'use-debounce';

import { useGetDocIds } from '@/api/queries/useGetDocIds';
import { DocIds, DocIdsWithCount } from '@/api/types/backend-dto-types';
import { Icon } from '@/new/components/icon';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/new/components/shadcn/command';
import { Popover, PopoverContent, PopoverAnchor } from '@/new/components/shadcn/popover';
import { cn } from '@/new/lib/utils';

interface DocMentionsInputProps {
  value: string; // Internal format: @[fileName](docId)
  onChange: (value: string) => void;
  onSubmit?: () => void;
  onEscape?: () => void;
  onPlaceholdersChange?: (hasPlaceholders: boolean) => void;
  onFocus?: () => void;
  matterId: string;
  subsetId: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  popoverAnchorRef?: React.RefObject<HTMLElement>;
}

const sanitizeFileName = (fileName: string) => {
  return fileName.replace(/[[\]()]/g, '');
};

export interface KimMentions {
  documents: string[];
  folders: string[];
}

export const folderMentionPrefix = 'folder:';
export const documentMentionPrefix = 'document:';

const createTokenRegex = () => /@\[(.*?)\]\((.*?)\)|%\[([^[\]]*?)\]/g;

export const extractKimMentions = (value: string): KimMentions => {
  const documents = new Set<string>();
  const folders = new Set<string>();
  const tokenRegex = createTokenRegex();
  let match;

  while ((match = tokenRegex.exec(value)) !== null) {
    if (match[2]?.startsWith(folderMentionPrefix)) {
      folders.add(match[2].slice(folderMentionPrefix.length));
    } else if (match[2]?.startsWith(documentMentionPrefix)) {
      documents.add(match[2].slice(documentMentionPrefix.length));
    } else if (match[2]) {
      documents.add(match[2]);
    }
  }

  return {
    documents: Array.from(documents),
    folders: Array.from(folders),
  };
};

type FolderMention = DocIdsWithCount['folders'][number];
type MentionOption = { type: 'document'; doc: DocIds } | { type: 'folder'; folder: FolderMention };

const getFolderName = (folder: FolderMention) => folder.path.split('/').filter(Boolean).pop() ?? folder.path;
const getFolderParentPath = (folder: FolderMention) => {
  const pathParts = folder.path.split('/').filter(Boolean);
  return pathParts.length > 1 ? pathParts.slice(0, -1).join('/') : '';
};

const renderMentionOptionLabel = (option: MentionOption) => {
  if (option.type === 'document') {
    return <span className="truncate">{sanitizeFileName(option.doc.file.name)}</span>;
  }

  const parentPath = getFolderParentPath(option.folder);

  return (
    <span className="flex min-w-0 items-baseline gap-1.5">
      <span className="truncate">{sanitizeFileName(getFolderName(option.folder))}</span>
      {parentPath && <span className="truncate text-gray-400">{sanitizeFileName(parentPath)}/</span>}
    </span>
  );
};

export interface DocMentionsInputHandle {
  focus: () => void;
  triggerMention: () => void;
}

export const DocMentionsInput = forwardRef<DocMentionsInputHandle, DocMentionsInputProps>(function DocMentionsInput(
  {
    value,
    onChange,
    onSubmit,
    onEscape,
    onPlaceholdersChange,
    onFocus,
    matterId,
    subsetId,
    placeholder = 'Ask Kim...',
    className,
    disabled = false,
    autoFocus = false,
    popoverAnchorRef,
  },
  ref,
) {
  const [showMentions, setShowMentions] = useState(false);
  const [commandSearch, setCommandSearch] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const editorRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const mentionCursorValuePosRef = useRef<number | null>(null);
  const isPointerDownInPopoverRef = useRef(false);
  const shouldScrollSelectedOptionIntoViewRef = useRef(false);
  // Tracks the last value set by user input, so we can skip innerHTML replacement for self-triggered changes
  const lastInputValueRef = useRef(value);
  const hasPlaceholdersRef = useRef(false);

  const reportPlaceholders = () => {
    if (!editorRef.current) return;
    const has = editorRef.current.querySelectorAll('.placeholder').length > 0;
    if (has !== hasPlaceholdersRef.current) {
      hasPlaceholdersRef.current = has;
      onPlaceholdersChange?.(has);
    }
  };

  useImperativeHandle(ref, () => ({
    focus: () => editorRef.current?.focus(),
    triggerMention: () => {
      if (!editorRef.current) return;
      editorRef.current.focus();

      const visibleText = editorRef.current.textContent || '';
      setCursorPosition(visibleText.length);

      const lastChar = visibleText.length > 0 ? visibleText[visibleText.length - 1] : '';
      const needsSpace = visibleText.length > 0 && lastChar !== ' ' && lastChar !== '\u00A0' && lastChar !== '\n';

      document.execCommand('insertText', false, needsSpace ? ' @' : '@');
    },
  }));

  // Fetch documents
  const [debouncedSearch] = useDebounce(commandSearch, 200);
  const hasActiveSearch = debouncedSearch.trim().length > 0;
  const { data: initialDocs, isLoading: isLoadingInitialDocs } = useGetDocIds(matterId, subsetId, { limit: 20 });
  const {
    data: searchDocs,
    isFetching: isSearching,
    isLoading: isLoadingSearchDocs,
  } = useGetDocIds(matterId, subsetId, {
    limit: 20,
    searchQuery: debouncedSearch || undefined,
    keepPreviousData: true,
  });
  const isWaitingForDebounce = commandSearch !== debouncedSearch;
  const docs = hasActiveSearch ? (searchDocs?.documents ?? []) : (initialDocs?.documents ?? []);
  const folders = hasActiveSearch ? (searchDocs?.folders ?? []) : (initialDocs?.folders ?? []);
  const mentionOptions: MentionOption[] = [
    ...folders.map((folder) => ({ type: 'folder' as const, folder })),
    ...docs.map((doc) => ({ type: 'document' as const, doc })),
  ];
  const isLoading = hasActiveSearch ? isLoadingSearchDocs : isLoadingInitialDocs;
  const isRefreshingResults = isWaitingForDebounce || (hasActiveSearch && isSearching);
  const hasVisibleStaleOptions = mentionOptions.length > 0 && isRefreshingResults;
  const showLoadingState = isLoading || (mentionOptions.length === 0 && isRefreshingResults);
  const mentionableItemsLabel = 'documents and folders';
  const loadingLabel = hasActiveSearch
    ? `Searching ${mentionableItemsLabel}...`
    : `Loading ${mentionableItemsLabel}...`;

  // Auto-focus the editor on mount
  useEffect(() => {
    if (autoFocus && editorRef.current) {
      editorRef.current.focus();
    }
  }, []);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [commandSearch]);

  useEffect(() => {
    if (mentionOptions.length === 0) {
      setSelectedIndex(0);
      return;
    }

    setSelectedIndex((prev) => Math.min(prev, mentionOptions.length - 1));
  }, [mentionOptions.length]);

  // Scroll selected item into view
  useEffect(() => {
    if (!showMentions || !listRef.current) return;
    if (!shouldScrollSelectedOptionIntoViewRef.current) return;
    shouldScrollSelectedOptionIntoViewRef.current = false;

    const item = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
    if (item) {
      (item as HTMLElement).scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, showMentions]);

  const moveSelectedIndexWithKeyboard = (getNextIndex: (currentIndex: number) => number) => {
    setSelectedIndex((prev) => {
      const next = getNextIndex(prev);
      shouldScrollSelectedOptionIntoViewRef.current = next !== prev;
      return next;
    });
  };

  // Convert internal value format (@[fileName](docId) and %[placeholder]) to display HTML
  const valueToHTML = (val: string): string => {
    if (!val) return '';

    const escapeHTML = (str: string) => {
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    };

    // Combined regex: mentions OR placeholders
    const tokenRegex = createTokenRegex();
    let lastIndex = 0;
    let result = '';

    let match;
    while ((match = tokenRegex.exec(val)) !== null) {
      result += escapeHTML(val.slice(lastIndex, match.index));

      if (match[1] !== undefined) {
        const fileName = match[1];
        const mentionId = match[2];
        const mentionType = mentionId.startsWith(folderMentionPrefix) ? 'folder' : 'document';

        result += `<span class="mention group/mention" data-mention-id="${mentionId}" data-mention-type="${mentionType}" data-file-name="${escapeHTML(fileName)}" contenteditable="false"><span class="bg-purple-50 text-purple-500 py-0.5 -my-0.5 rounded-md px-1 [box-decoration-break:clone] [-webkit-box-decoration-break:clone]">@${escapeHTML(sanitizeFileName(fileName))}</span><span class="mention-remove inline-flex pl-1 items-center justify-center overflow-hidden w-0 h-3.5 opacity-0 group-hover/mention:w-3.5 group-hover/mention:opacity-100 rounded-full text-purple-400 hover:text-purple-600 transition-all duration-150 cursor-pointer after:content-['×']" data-mention-remove="true"></span></span>`;
      } else if (match[3] !== undefined) {
        const text = match[3];
        const escapedText = escapeHTML(text);
        result += `<span class="placeholder bg-green-50 text-green-500 duration-150 transition-colors rounded-sm px-1 py-0.5 -my-0.5 cursor-pointer hover:bg-green-100/70 [box-decoration-break:clone] [-webkit-box-decoration-break:clone]" data-placeholder="true" data-original-text="${escapedText}" contenteditable="false">[${escapedText}]</span>`;
      }

      lastIndex = match.index + match[0].length;
    }

    result += escapeHTML(val.slice(lastIndex));
    result = result.replace(/\n/g, '<br>');

    return result;
  };

  const textWithLineBreaks = (root: HTMLElement): string => {
    const blockTags = new Set(['DIV', 'P', 'LI']);
    let result = '';

    const appendNewline = () => {
      if (result && !result.endsWith('\n')) result += '\n';
    };

    const visit = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        result += node.textContent ?? '';
        return;
      }

      if (node.nodeName === 'BR') {
        result += '\n';
        return;
      }

      const isBlock = node !== root && blockTags.has(node.nodeName);
      if (isBlock) appendNewline();

      node.childNodes.forEach(visit);

      if (isBlock) appendNewline();
    };

    visit(root);

    return result.endsWith('\n') ? result.slice(0, -1) : result;
  };

  // Convert display HTML back to internal format
  const htmlToValue = (html: string): string => {
    const temp = document.createElement('div');
    temp.innerHTML = html;

    // Process placeholders: edited ones become plain text, unedited ones round-trip as %[text]
    const placeholders = temp.querySelectorAll('.placeholder');
    placeholders.forEach((ph) => {
      const originalText = ph.getAttribute('data-original-text');
      const isEditing = ph.classList.contains('editing');
      const currentText = ph.textContent || '';

      if (isEditing && originalText !== null && currentText !== `[${originalText}]`) {
        ph.replaceWith(document.createTextNode(currentText));
      } else if (originalText !== null) {
        ph.replaceWith(document.createTextNode(`%[${originalText}]`));
      }
    });

    const mentions = temp.querySelectorAll('.mention');
    mentions.forEach((mention) => {
      const fileName = mention.getAttribute('data-file-name');
      const mentionId = mention.getAttribute('data-mention-id');
      const textNode = document.createTextNode(`@[${fileName}](${mentionId})`);
      mention.replaceWith(textNode);
    });

    return textWithLineBreaks(temp);
  };

  // Get cursor position in text content (visible text)
  const getCursorPosition = (): number => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editorRef.current) return 0;

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(editorRef.current);
    preCaretRange.setEnd(range.endContainer, range.endOffset);

    return preCaretRange.toString().length;
  };

  // Convert cursor position from visible text to internal value format
  const mapVisiblePosToValuePos = (visiblePos: number, visibleText: string, internalValue: string): number => {
    const tokenRegex = createTokenRegex();
    let valuePos = 0;
    let currentVisiblePos = 0;
    let match;
    let lastIndex = 0;

    while ((match = tokenRegex.exec(internalValue)) !== null) {
      const tokenStart = match.index;
      const tokenEnd = match.index + match[0].length;

      const beforeToken = internalValue.slice(lastIndex, tokenStart);
      const beforeLength = beforeToken.length;

      if (currentVisiblePos + beforeLength >= visiblePos) {
        return valuePos + (visiblePos - currentVisiblePos);
      }

      valuePos += beforeLength;
      currentVisiblePos += beforeLength;

      // Compute visible length for this token
      let visibleTokenLength: number;
      if (match[1] !== undefined) {
        // Mention: @sanitizedFileName
        visibleTokenLength = sanitizeFileName(match[1]).length + 1;
      } else {
        // Placeholder: [text]
        visibleTokenLength = (match[3] ?? '').length + 2;
      }

      if (currentVisiblePos + visibleTokenLength >= visiblePos) {
        return tokenEnd;
      }

      valuePos = tokenEnd;
      currentVisiblePos += visibleTokenLength;
      lastIndex = tokenEnd;
    }

    return valuePos + (visiblePos - currentVisiblePos);
  };

  // Set cursor position
  const setCursorPosition = (pos: number) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    const range = document.createRange();

    let currentPos = 0;
    let targetNode: Node | null = null;
    let targetOffset = 0;

    const walker = document.createTreeWalker(editorRef.current, NodeFilter.SHOW_TEXT);

    while (walker.nextNode()) {
      const node = walker.currentNode;
      const textLength = node.textContent?.length || 0;

      if (currentPos + textLength >= pos) {
        targetNode = node;
        targetOffset = pos - currentPos;
        break;
      }

      currentPos += textLength;
    }

    if (targetNode) {
      range.setStart(targetNode, targetOffset);
      range.collapse(true);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };

  // Handle clicks on mention remove buttons and placeholder chips
  const handleEditorMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;

    // Placeholder click: make editable and select inner text
    const placeholderEl = target.closest('.placeholder') as HTMLElement | null;
    if (placeholderEl && !placeholderEl.classList.contains('editing')) {
      e.preventDefault();
      e.stopPropagation();

      placeholderEl.classList.add('editing');
      placeholderEl.setAttribute('contenteditable', 'true');

      // Select all content inside the placeholder so the next keystroke replaces it
      const range = document.createRange();
      range.selectNodeContents(placeholderEl);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);

      placeholderEl.focus();
      return;
    }

    const removeBtn = target.hasAttribute('data-mention-remove') ? target : target.closest('[data-mention-remove]');
    if (!removeBtn) return;

    e.preventDefault();
    e.stopPropagation();

    const mentionEl = removeBtn.closest('.mention');
    if (!mentionEl) return;

    const mentionId = mentionEl.getAttribute('data-mention-id');
    const fileName = mentionEl.getAttribute('data-file-name');
    if (!mentionId || !fileName) return;

    const escapedFileName = fileName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const escapedMentionId = mentionId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const mentionRegex = new RegExp(`@\\[${escapedFileName}\\]\\(${escapedMentionId}\\)\\s?`);

    const mentionMatch = mentionRegex.exec(value);
    if (!mentionMatch) return;

    const currentCursorPos = getCursorPosition();

    // Calculate visible position where the mention starts
    const beforeMention = value.slice(0, mentionMatch.index);
    const innerRegex = /@\[(.*?)\]\((.*?)\)/g;
    let visibleMentionStart = 0;
    let lastIdx = 0;
    let m;
    while ((m = innerRegex.exec(beforeMention)) !== null) {
      visibleMentionStart += m.index - lastIdx;
      visibleMentionStart += sanitizeFileName(m[1]).length + 1;
      lastIdx = m.index + m[0].length;
    }
    visibleMentionStart += beforeMention.length - lastIdx;

    // Calculate visible length of what's being removed
    const visibleMentionLen = sanitizeFileName(fileName).length + 1;
    const hasTrailingSpace = mentionMatch[0].length > `@[${fileName}](${mentionId})`.length;
    const totalVisibleRemoved = visibleMentionLen + (hasTrailingSpace ? 1 : 0);

    // Adjust cursor: shift back if it was after the mention, otherwise keep it
    const newCursorPos =
      currentCursorPos <= visibleMentionStart ? currentCursorPos : currentCursorPos - totalVisibleRemoved;

    const newValue = value.replace(mentionRegex, '');
    onChange(newValue);

    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.focus();
        setCursorPosition(Math.max(0, newCursorPos));
      }
    }, 0);
  };

  // Handle input changes
  const handleInput = () => {
    if (!editorRef.current) return;

    const html = editorRef.current.innerHTML;
    const textValue = htmlToValue(html);
    const visibleCursorPos = getCursorPosition();
    const visibleText = editorRef.current.textContent || '';

    // Detect placeholder promotion: an editing placeholder whose content has changed.
    // In that case we want the value-sync useEffect to re-render the DOM (stripping
    // the chip wrapper), so we leave lastInputValueRef untouched to force the diff.
    const editingPlaceholders = editorRef.current.querySelectorAll('.placeholder.editing');
    let placeholderPromoted = false;
    editingPlaceholders.forEach((ph) => {
      const originalText = ph.getAttribute('data-original-text');
      const currentText = ph.textContent || '';
      if (originalText !== null && currentText !== `[${originalText}]`) {
        placeholderPromoted = true;
      }
    });

    if (!placeholderPromoted) {
      lastInputValueRef.current = textValue;
    }
    onChange(textValue);
    reportPlaceholders();

    // Check for @ trigger
    const textBeforeCursor = textValue.slice(0, textValue.length) || '';
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';

      const afterAt = textBeforeCursor.slice(lastAtIndex);
      const isMentionFormat = /^@\[.*?\]\(.*?\)/.test(afterAt);

      const isWhitespaceBefore = charBeforeAt === ' ' || charBeforeAt === '\u00A0' || charBeforeAt === '\n';

      if (!isMentionFormat && (isWhitespaceBefore || lastAtIndex === 0)) {
        const searchText = textBeforeCursor.slice(lastAtIndex + 1);
        if (!searchText.includes('\n') && !/[\s\u00A0]{2}/.test(searchText)) {
          const valueAtPos = mapVisiblePosToValuePos(
            visibleText.lastIndexOf('@', visibleCursorPos),
            visibleText,
            textValue,
          );
          const valueCursorPos = mapVisiblePosToValuePos(visibleCursorPos, visibleText, textValue);
          setCommandSearch(searchText);
          setMentionStartIndex(valueAtPos);
          mentionCursorValuePosRef.current = valueCursorPos;
          setShowMentions(true);
          return;
        }
      }
    }

    mentionCursorValuePosRef.current = null;
    setMentionStartIndex(null);
    setShowMentions(false);
  };

  // Insert mention
  const insertMention = (option: MentionOption) => {
    if (!editorRef.current || mentionStartIndex === null || mentionCursorValuePosRef.current === null) return;
    const mentionCursorValuePos = mentionCursorValuePosRef.current;

    setShowMentions(false);
    setMentionStartIndex(null);
    mentionCursorValuePosRef.current = null;

    setTimeout(() => {
      setCommandSearch('');
    }, 150);

    const currentValue = htmlToValue(editorRef.current.innerHTML);
    const before = currentValue.slice(0, mentionStartIndex);
    const after = currentValue.slice(mentionCursorValuePos);
    const label = option.type === 'folder' ? getFolderName(option.folder) : option.doc.file.name;
    const mentionId =
      option.type === 'folder'
        ? `${folderMentionPrefix}${option.folder.id}`
        : `${documentMentionPrefix}${option.doc.id}`;
    const newValue = `${before}@[${label}](${mentionId})\u00A0${after}`;

    onChange(newValue);

    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = valueToHTML(newValue);

        let visibleBeforeLength = 0;
        const tokenRegex = createTokenRegex();
        let lastIndex = 0;
        let match;

        while ((match = tokenRegex.exec(before)) !== null) {
          visibleBeforeLength += match.index - lastIndex;
          if (match[1] !== undefined) {
            visibleBeforeLength += sanitizeFileName(match[1]).length + 1; // +1 for @
          } else {
            visibleBeforeLength += (match[3] ?? '').length + 2; // 2 brackets
          }
          lastIndex = match.index + match[0].length;
        }
        visibleBeforeLength += before.length - lastIndex;

        const newVisibleCursorPos = visibleBeforeLength + sanitizeFileName(label).length + 2; // +2 for @ and space
        setCursorPosition(newVisibleCursorPos);
        editorRef.current.focus();
      }
    }, 0);
  };

  // Strip rich text on paste — only allow plain text
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  // Handle keyboard events
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (showMentions) {
      if (mentionOptions.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          moveSelectedIndexWithKeyboard((prev) => (prev + 1) % mentionOptions.length);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          moveSelectedIndexWithKeyboard((prev) => (prev - 1 + mentionOptions.length) % mentionOptions.length);
        } else if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          if (hasVisibleStaleOptions) return;
          insertMention(mentionOptions[selectedIndex]);
        } else if (e.key === 'Escape') {
          setShowMentions(false);
        }
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
      } else if (e.key === 'Escape') {
        setShowMentions(false);
      }
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (hasPlaceholdersRef.current) return;
      onSubmit?.();
    } else if (e.key === 'Enter' && e.shiftKey) {
      // Allow default behavior (newline via contentEditable)
    } else if (e.key === 'Escape') {
      onEscape?.();
    } else if (e.key === 'Backspace') {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        if (range.collapsed && range.startOffset === 0) {
          const previousNode = range.startContainer.previousSibling as Element | null;
          const prevClasses = previousNode?.classList;

          if (prevClasses?.contains('mention') || prevClasses?.contains('placeholder')) {
            e.preventDefault();
            previousNode?.remove();
            handleInput();
          }
        }
      }
    }
  };

  const isEmpty = !value || value.trim() === '';

  // Revert any placeholders that were opened for editing but never modified
  const revertUneditedPlaceholders = () => {
    if (!editorRef.current) return;
    const editing = editorRef.current.querySelectorAll('.placeholder.editing');
    editing.forEach((ph) => {
      const originalText = ph.getAttribute('data-original-text');
      const currentText = ph.textContent || '';
      if (originalText !== null && currentText === `[${originalText}]`) {
        ph.classList.remove('editing');
        ph.setAttribute('contenteditable', 'false');
      }
    });
  };

  const handleEditorBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const nextFocused = e.relatedTarget;

    if (isPointerDownInPopoverRef.current) {
      revertUneditedPlaceholders();
      requestAnimationFrame(() => {
        isPointerDownInPopoverRef.current = false;
      });
      return;
    }

    if (
      nextFocused instanceof Node &&
      (popoverContentRef.current?.contains(nextFocused) || editorRef.current?.contains(nextFocused))
    ) {
      revertUneditedPlaceholders();
      return;
    }

    requestAnimationFrame(() => {
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (popoverContentRef.current?.contains(activeElement) || editorRef.current?.contains(activeElement))
      ) {
        return;
      }

      setShowMentions(false);
      setMentionStartIndex(null);
      mentionCursorValuePosRef.current = null;
      revertUneditedPlaceholders();
    });
  };

  const closeMentions = () => {
    setShowMentions(false);
    setMentionStartIndex(null);
    mentionCursorValuePosRef.current = null;
    isPointerDownInPopoverRef.current = false;
  };

  const handleCommandInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (mentionOptions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        moveSelectedIndexWithKeyboard((prev) => (prev + 1) % mentionOptions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        moveSelectedIndexWithKeyboard((prev) => (prev - 1 + mentionOptions.length) % mentionOptions.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (hasVisibleStaleOptions) return;
        insertMention(mentionOptions[selectedIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closeMentions();
        editorRef.current?.focus();
      }
      return;
    }

    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeMentions();
      editorRef.current?.focus();
    }
  };

  // Update display when value changes externally (e.g. mention removal, workflow insertion).
  // Skips innerHTML replacement for user-typed input to preserve DOM state (hover, transitions).
  useEffect(() => {
    if (editorRef.current && value !== lastInputValueRef.current) {
      const wasEmpty = !lastInputValueRef.current;
      lastInputValueRef.current = value;
      const cursorPos = getCursorPosition();
      const wasFocused =
        document.activeElement === editorRef.current || editorRef.current.contains(document.activeElement);
      editorRef.current.innerHTML = valueToHTML(value);

      if (wasFocused) {
        if (wasEmpty && value) {
          const visibleText = editorRef.current.textContent || '';
          setCursorPosition(visibleText.length);
        } else {
          setCursorPosition(cursorPos);
        }
      }
      reportPlaceholders();
    }
  }, [value]);

  return (
    <Popover open={showMentions}>
      {popoverAnchorRef ? (
        <>
          <PopoverAnchor virtualRef={popoverAnchorRef} />
          <div
            ref={editorRef}
            contentEditable={!disabled}
            onBlur={handleEditorBlur}
            onFocus={onFocus}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onMouseDown={handleEditorMouseDown}
            onPaste={handlePaste}
            data-placeholder={placeholder}
            suppressContentEditableWarning
            className={cn(
              'relative overflow-y-auto rounded-xl border-0 ring-0 before:absolute focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none',
              isEmpty && 'before:text-gray-400/80 before:content-[attr(data-placeholder)]',
              disabled && 'cursor-not-allowed border-gray-200/60 bg-[#fcfcfc] text-gray-300',
              disabled && isEmpty && 'before:text-gray-300/80',
              className,
            )}
          />
        </>
      ) : (
        <PopoverAnchor asChild>
          <div
            ref={editorRef}
            contentEditable={!disabled}
            onBlur={handleEditorBlur}
            onFocus={onFocus}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onMouseDown={handleEditorMouseDown}
            onPaste={handlePaste}
            data-placeholder={placeholder}
            suppressContentEditableWarning
            className={cn(
              'relative overflow-y-auto rounded-xl border-0 ring-0 before:absolute focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none',
              isEmpty && 'before:text-gray-400/80 before:content-[attr(data-placeholder)]',
              disabled && 'cursor-not-allowed border-gray-200/60 bg-[#fcfcfc] text-gray-300',
              disabled && isEmpty && 'before:text-gray-300/80',
              className,
            )}
          />
        </PopoverAnchor>
      )}

      <PopoverContent
        ref={popoverContentRef}
        className="mb-1 w-[var(--radix-popover-trigger-width)] max-w-none rounded-xl p-0 shadow-lg/2"
        align="start"
        side="top"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onPointerDownCapture={() => {
          isPointerDownInPopoverRef.current = true;
        }}
        onInteractOutside={() => {
          closeMentions();
        }}
      >
        <Command shouldFilter={false} disablePointerSelection>
          <CommandInput
            placeholder={`Search ${mentionableItemsLabel}...`}
            value={commandSearch}
            onValueChange={setCommandSearch}
            onKeyDown={handleCommandInputKeyDown}
          />

          <div className="relative overflow-hidden">
            <CommandList ref={listRef} className={cn('max-h-53', hasVisibleStaleOptions && 'animate-pulse')}>
              <CommandEmpty className="pt-8">
                {showLoadingState ? (
                  <span className="flex items-center justify-center gap-2 text-gray-400">
                    <Icon className="lucide--loader animate-spin ease-in-out" />
                    {loadingLabel}
                  </span>
                ) : (
                  <>
                    No {mentionableItemsLabel} found matching &ldquo;{commandSearch}&rdquo;
                  </>
                )}
              </CommandEmpty>
              <CommandGroup className={cn(hasVisibleStaleOptions && 'pointer-events-none')}>
                {mentionOptions.map((option, index) => (
                  <CommandItem
                    key={`${option.type}-${option.type === 'folder' ? option.folder.id : option.doc.id}`}
                    value={option.type === 'folder' ? option.folder.path : option.doc.file.name}
                    data-index={index}
                    disabled={hasVisibleStaleOptions}
                    onSelect={() => insertMention(option)}
                    onMouseMove={() => {
                      shouldScrollSelectedOptionIntoViewRef.current = false;
                      setSelectedIndex(index);
                    }}
                    className={cn(
                      'cursor-pointer gap-3 pl-2.5 text-gray-600',
                      index === selectedIndex && 'bg-gray-50 text-gray-900',
                    )}
                  >
                    <Icon
                      className={cn(
                        'text-[13px] text-gray-400',
                        option.type === 'folder' ? 'lucide--folder-closed' : 'lucide--file-text',
                      )}
                    />{' '}
                    {renderMentionOptionLabel(option)}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
});
